import { RiskClassification } from 'modules/basilisk/basilisk.model';
import { ContestacaoJobResult } from 'modules/contestacao/contestacao.model';
import {
	Job,
	JobBilling,
	JobCreatedBy,
	JobDetail,
	JobDetailDocument,
	JobDetailSetupSnapshot,
	JobFailure,
	JobFile,
	JobFileType,
	JobProductSlug,
	JobResultField,
	JobsMonthlyUsage,
	JobStatusLabel,
	JobTimings,
	LimitType,
} from 'services/jobs/jobs.model';
import { ProductSetupDetail } from 'services/product-setup/product-setup.model';
import { buildCurrentUser } from '../session/session.mock';
import { clientsDb, fileTypesDb, productSetupsDb } from '../context/context.const';
import {
	MOCK_CATALOG,
	MOCK_CONTEXT_CLIENT,
	MOCK_PEOPLE,
	MOCK_SAMPLE_PDF_URL,
	MOCK_VERTICAL_INFO,
} from '../shared/mock-context.const';
import { allJobEntries, currentJobStatus, findJobEntry } from '../shared/mock-job-state';
import { MockJobRosterEntry } from '../shared/mock-jobs-roster.const';
import { generatedProfileOf } from './jobs-generated.const';
import {
	admissionResult,
	DAY_MS,
	FT,
	HOUR_MS,
	MINUTE_MS,
	MOCK_EPOCH_MS,
	MockJobDocumentSeed,
	MockJobProfile,
	pdf,
} from './jobs-profile.const';

export { MOCK_EPOCH_MS } from './jobs-profile.const';
export type { MockJobDocumentSeed, MockJobProfile } from './jobs-profile.const';

/**
 * Dados dos jobs mock (caixa de entrada, detalhe, overview e dashboards).
 * Os ids/status vêm do roster compartilhado; aqui ficam só os campos extras
 * de cada job (documentos, tempos, falha, metadados e resultado da extração).
 */

export type MockPersonKey = MockJobRosterEntry['createdBy'];

export const PRODUCT_SUCCESS_STATUS: Record<JobProductSlug, JobStatusLabel> = {
	cadastro: 'Finalizado',
	contestacao: 'Aguardando revisão',
	basilisk: 'Aguardando revisão',
};

const POST_PROCESSING_STATUSES: ReadonlySet<JobStatusLabel> = new Set<JobStatusLabel>([
	'Aguardando revisão',
	'Em revisão',
	'Finalizado',
]);

// ── Perfis dos jobs escritos à mão ──────────────────────────────────────────

export const MOCK_JOB_PROFILES: Record<string, MockJobProfile> = {
	// ── Cadastro ───────────────────────────────────────────────────────────────
	'job-cd-001': {
		minutesAgo: 42,
		queueMs: 3_200,
		processingMs: 48_500,
		reviewMs: 0,
		documents: [
			pdf('ficha-admissao-joao-pereira.pdf', FT.contratoTrabalho, 3, 412_880, 'Ficha de admissão'),
			pdf('rg-cpf-joao-pereira.pdf', FT.documentoIdentidade, 1, 180_331),
		],
		result: admissionResult({
			nome: 'João Pereira da Silva',
			cpf: '123.456.789-09',
			rg: '32.415.678-9 SSP/SP',
			nascimento: '14/03/1991',
			admissao: '01/09/2026',
			cargo: 'Analista de Logística Pleno',
			cbo: '3421-10',
			departamento: 'Operações — Cargas',
			salario: 'R$ 5.480,00',
			pis: '120.54789.32-1',
			ctps: '4587123 — série 0012/SP',
			endereco: {
				logradouro: 'Rua Vergueiro, 1520, apto 83',
				bairro: 'Vila Mariana',
				cidade: 'São Paulo',
				uf: 'SP',
				cep: '04101-000',
			},
			dependentes: [
				{ nome: 'Lucas Pereira da Silva', parentesco: 'Filho', nascimento: '22/07/2019' },
			],
		}),
	},
	'job-cd-002': {
		minutesAgo: 3,
		queueMs: 1_800,
		processingMs: 62_000,
		reviewMs: 0,
		documents: [
			pdf('ctps-mariana-souza.pdf', FT.documentoIdentidade, 4, 1_204_551, 'CTPS'),
			pdf('contrato-trabalho-mariana-souza.pdf', FT.contratoTrabalho, 6, 689_020),
		],
		result: admissionResult({
			nome: 'Mariana Souza Oliveira',
			cpf: '987.654.321-00',
			rg: '45.789.123-4 SSP/SP',
			nascimento: '02/11/1995',
			admissao: '15/09/2026',
			cargo: 'Comissária de Bordo',
			cbo: '5111-10',
			departamento: 'Tripulação — Base GRU',
			salario: 'R$ 3.950,00',
			pis: '134.87965.21-7',
			ctps: '7845120 — série 0045/SP',
			endereco: {
				logradouro: 'Av. Tiradentes, 890',
				bairro: 'Centro',
				cidade: 'Guarulhos',
				uf: 'SP',
				cep: '07010-000',
			},
			dependentes: [],
		}),
	},
	'job-cd-003': {
		minutesAgo: 1,
		queueMs: 0,
		processingMs: 140_000,
		reviewMs: 0,
		documents: Array.from({ length: 12 }, (_, index) =>
			pdf(
				`lote-admissional-${String(index + 1).padStart(2, '0')}.pdf`,
				FT.contratoTrabalho,
				2,
				240_000 + index * 13_517,
				'Ficha de admissão',
			),
		),
		result: {
			total_documentos: 12,
			colaboradores: [
				{
					nome: 'Felipe Andrade Costa',
					cpf: '321.654.987-11',
					cargo: 'Mecânico de Aeronaves',
					data_admissao: '22/09/2026',
				},
				{
					nome: 'Larissa Moura Teixeira',
					cpf: '741.852.963-22',
					cargo: 'Agente de Aeroporto',
					data_admissao: '22/09/2026',
				},
				{
					nome: 'Thiago Nascimento Rocha',
					cpf: '159.357.486-33',
					cargo: 'Auxiliar de Rampa',
					data_admissao: '23/09/2026',
				},
			],
		},
	},
	'job-cd-004': {
		minutesAgo: 185,
		queueMs: 2_400,
		processingMs: 36_900,
		reviewMs: 0,
		documents: [
			pdf('trct-carlos-henrique-almeida.pdf', FT.termoRescisao, 2, 356_112),
			pdf('aviso-previo-carlos-henrique.pdf', FT.termoRescisao, 1, 98_440, 'Aviso prévio'),
		],
		result: {
			nome_completo: 'Carlos Henrique Almeida',
			cpf: '456.789.123-45',
			data_admissao: '03/02/2021',
			data_desligamento: '31/08/2026',
			cargo: 'Supervisor de Manutenção',
			motivo_rescisao: 'Dispensa sem justa causa',
			aviso_previo: 'Indenizado — 42 dias',
			ultimo_salario: 'R$ 7.320,00',
			verbas_rescisorias: {
				saldo_salario: 'R$ 7.320,00',
				ferias_vencidas: 'R$ 9.760,00',
				ferias_proporcionais: 'R$ 4.880,00',
				decimo_terceiro_proporcional: 'R$ 4.880,00',
				aviso_previo_indenizado: 'R$ 10.248,00',
				multa_fgts_40: 'R$ 11.713,92',
			},
			total_liquido: 'R$ 43.215,48',
			homologado: true,
		},
	},
	'job-cd-005': {
		minutesAgo: 320,
		queueMs: 4_100,
		processingMs: 21_700,
		reviewMs: 0,
		documents: [
			pdf('holerite-jun-2026-digitalizado.pdf', FT.holerite, 1, 2_310_774),
			pdf('holerite-jul-2026-digitalizado.pdf', FT.holerite, 1, 2_287_019),
			pdf('holerite-ago-2026-digitalizado.pdf', FT.holerite, 1, 2_401_338),
		],
		failure: {
			reason: 'LOW_IMAGE_QUALITY',
			message:
				'A digitalização dos holerites está abaixo da resolução mínima (72 dpi). Reenvie os arquivos com pelo menos 200 dpi.',
		},
		result: {
			colaborador: 'Patrícia Gomes Ribeiro',
			cpf: '258.369.147-66',
			competencias: [
				{
					mes: '06/2026',
					salario_bruto: 'R$ 4.200,00',
					descontos: 'R$ 812,40',
					liquido: 'R$ 3.387,60',
				},
				{
					mes: '07/2026',
					salario_bruto: 'R$ 4.200,00',
					descontos: 'R$ 812,40',
					liquido: 'R$ 3.387,60',
				},
				{
					mes: '08/2026',
					salario_bruto: 'R$ 4.620,00',
					descontos: 'R$ 905,10',
					liquido: 'R$ 3.714,90',
				},
			],
		},
	},
	'job-cd-006': {
		minutesAgo: 95,
		queueMs: 2_900,
		processingMs: 51_300,
		reviewMs: 0,
		documents: [
			pdf(
				'ficha-admissao-beatriz-mendonca.pdf',
				FT.contratoTrabalho,
				3,
				398_205,
				'Ficha de admissão',
			),
			pdf('comprovante-residencia-beatriz.pdf', FT.comprovanteResidencia, 1, 145_902),
		],
		result: admissionResult({
			nome: 'Beatriz Mendonça Carvalho',
			cpf: '369.258.147-80',
			rg: '28.963.741-2 SSP/RJ',
			nascimento: '30/06/1988',
			admissao: '16/09/2026',
			cargo: 'Coordenadora de Recursos Humanos',
			cbo: '1422-05',
			departamento: 'Gente & Gestão',
			salario: 'R$ 9.150,00',
			pis: '109.36521.47-8',
			ctps: '3021458 — série 0088/RJ',
			endereco: {
				logradouro: 'Rua Voluntários da Pátria, 45, bloco B',
				bairro: 'Botafogo',
				cidade: 'Rio de Janeiro',
				uf: 'RJ',
				cep: '22270-000',
			},
			dependentes: [
				{ nome: 'Rodrigo Mendonça Carvalho', parentesco: 'Cônjuge', nascimento: '11/01/1986' },
				{ nome: 'Helena Mendonça Carvalho', parentesco: 'Filha', nascimento: '05/04/2017' },
			],
		}),
	},
	'job-cd-007': {
		minutesAgo: 610,
		queueMs: 12_800,
		processingMs: 74_200,
		reviewMs: 0,
		documents: [
			pdf('atestado-0912-rodrigues.pdf', FT.atestadoMedico, 1, 201_447),
			pdf('atestado-0913-figueiredo.pdf', FT.atestadoMedico, 1, 187_930),
			pdf('atestado-0914-santana.pdf', FT.atestadoMedico, 2, 256_108),
		],
		result: {
			atestados: [
				{
					colaborador: 'André Rodrigues',
					cid: 'J11 — Influenza',
					data_inicio: '12/09/2026',
					dias_afastamento: 3,
					medico: 'Dra. Camila Torres',
					crm: 'CRM/SP 145.872',
				},
				{
					colaborador: 'Sílvia Figueiredo',
					cid: 'M54.5 — Dor lombar baixa',
					data_inicio: '13/09/2026',
					dias_afastamento: 2,
					medico: 'Dr. Renato Luz',
					crm: 'CRM/SP 98.214',
				},
				{
					colaborador: 'Marcos Santana',
					cid: 'S93.4 — Entorse do tornozelo',
					data_inicio: '14/09/2026',
					dias_afastamento: 7,
					medico: 'Dr. Paulo Serra',
					crm: 'CRM/SP 120.556',
				},
			],
		},
	},
	'job-cd-008': {
		minutesAgo: 270,
		queueMs: 2_100,
		processingMs: 44_600,
		reviewMs: 0,
		documents: [pdf('contrato-experiencia-rafael-lima.pdf', FT.contratoTrabalho, 5, 512_764)],
		result: {
			nome_completo: 'Rafael Lima Barbosa',
			cpf: '147.258.369-52',
			cargo: 'Assistente Administrativo',
			tipo_contrato: 'Contrato de experiência',
			data_inicio: '01/09/2026',
			prazo_experiencia: '45 dias, prorrogável por mais 45',
			data_termino_previsto: '15/10/2026',
			salario_base: 'R$ 2.870,00',
			jornada: '40h semanais',
			local_trabalho: 'Sede administrativa — São Paulo/SP',
			clausula_prorrogacao: true,
		},
	},

	// ── Contestação ────────────────────────────────────────────────────────────
	'job-ct-001': {
		minutesAgo: 25,
		queueMs: 4_600,
		processingMs: 312_000,
		reviewMs: 3 * HOUR_MS,
		documents: [
			pdf('peticao-inicial-0010234-56.2026.5.02.0011.pdf', FT.peticaoInicial, 28, 3_412_880),
			pdf('cartoes-ponto-2024-2026.pdf', FT.subsidio, 36, 5_021_447),
			pdf('contrato-trabalho-reclamante.pdf', FT.subsidio, 6, 702_311),
		],
	},
	'job-ct-002': {
		minutesAgo: 140,
		queueMs: 3_900,
		processingMs: 287_400,
		reviewMs: 4 * HOUR_MS,
		documents: [
			pdf('peticao-inicial-0001187-22.2026.5.01.0045.pdf', FT.peticaoInicial, 34, 4_107_225),
			pdf('politica-compliance-assedio.pdf', FT.subsidio, 12, 1_220_450),
			pdf('atas-reuniao-equipe.pdf', FT.subsidio, 8, 890_117),
		],
	},
	'job-ct-003': {
		minutesAgo: 380,
		queueMs: 5_200,
		processingMs: 355_800,
		reviewMs: 5 * HOUR_MS + 20 * MINUTE_MS,
		documents: [
			pdf('peticao-inicial-0010876-41.2026.5.02.0078.pdf', FT.peticaoInicial, 22, 2_874_331),
			pdf('fichas-financeiras-paradigma.pdf', FT.subsidio, 14, 1_678_902),
			pdf('plano-cargos-salarios.pdf', FT.subsidio, 9, 1_004_556),
		],
	},
	'job-ct-004': {
		minutesAgo: 4,
		queueMs: 2_700,
		processingMs: 330_000,
		reviewMs: 0,
		documents: [
			pdf('peticao-inicial-0011420-09.2026.5.15.0032.pdf', FT.peticaoInicial, 31, 3_890_004),
			pdf('cat-comunicacao-acidente.pdf', FT.subsidio, 3, 412_775),
			pdf('laudo-medico-ocupacional.pdf', FT.subsidio, 7, 980_663),
		],
	},
	'job-ct-005': {
		minutesAgo: 210,
		queueMs: 3_100,
		processingMs: 18_400,
		reviewMs: 0,
		documents: [
			pdf('peticao-inicial-0010099-73.2026.5.02.0003.pdf', FT.peticaoInicial, 19, 2_441_890),
			pdf('contrato-prestacao-servicos-pj.pdf', FT.subsidio, 11, 1_302_774),
		],
		failure: {
			reason: 'DOCUMENT_UNREADABLE',
			message:
				'A petição inicial está protegida por senha e não pôde ser lida. Envie o arquivo sem proteção.',
		},
	},
	'job-ct-006': {
		minutesAgo: 1,
		queueMs: 0,
		processingMs: 300_000,
		reviewMs: 0,
		documents: [
			pdf('peticao-inicial-0012005-88.2026.5.02.0060.pdf', FT.peticaoInicial, 24, 3_004_118),
			pdf('ppra-pcmso-2025.pdf', FT.subsidio, 42, 6_120_337),
		],
	},
	'job-ct-007': {
		minutesAgo: 455,
		queueMs: 6_300,
		processingMs: 298_700,
		reviewMs: 2 * HOUR_MS + 45 * MINUTE_MS,
		documents: [
			pdf('peticao-inicial-0010543-17.2026.5.02.0024.pdf', FT.peticaoInicial, 17, 2_101_556),
			pdf('trct-homologado.pdf', FT.subsidio, 2, 334_209),
			pdf('comprovante-pagamento-verbas.pdf', FT.subsidio, 1, 120_874),
		],
	},

	// ── Basilisk ───────────────────────────────────────────────────────────────
	'job-bs-001': {
		minutesAgo: 18,
		queueMs: 2_200,
		processingMs: 71_400,
		reviewMs: 2 * HOUR_MS,
		documents: [pdf('atestado-medico-0423.pdf', FT.atestadoMedico, 1, 845_120)],
		metadata: {
			cnj: '0010234-56.2026.5.02.0011',
			legalboxContencioso: 'LBC-2026-004512',
			severity: RiskClassification.HIGH,
		},
	},
	'job-bs-002': {
		minutesAgo: 160,
		queueMs: 1_900,
		processingMs: 58_300,
		reviewMs: 3 * HOUR_MS,
		documents: [pdf('comprovante-residencia.pdf', FT.comprovanteResidencia, 1, 612_004)],
		metadata: {
			cnj: '0001187-22.2026.5.01.0045',
			legalboxContencioso: 'LBC-2026-004377',
			severity: RiskClassification.MEDIUM,
		},
	},
	'job-bs-003': {
		minutesAgo: 400,
		queueMs: 2_600,
		processingMs: 66_900,
		reviewMs: HOUR_MS + 35 * MINUTE_MS,
		documents: [pdf('holerite-marco-2026.pdf', FT.holerite, 2, 478_330)],
		metadata: {
			cnj: '0010876-41.2026.5.02.0078',
			legalboxContencioso: 'LBC-2026-003981',
			severity: RiskClassification.CLEAN,
		},
	},
	'job-bs-004': {
		minutesAgo: 2,
		queueMs: 1_500,
		processingMs: 80_000,
		reviewMs: 0,
		documents: [pdf('ctps-digital.pdf', FT.documentoIdentidade, 3, 1_102_447, 'CTPS')],
		metadata: { legalboxContencioso: 'LBC-2026-004598' },
	},
	'job-bs-005': {
		minutesAgo: 230,
		queueMs: 2_000,
		processingMs: 14_800,
		reviewMs: 0,
		documents: [pdf('foto-documento-borrada.pdf', FT.documentoIdentidade, 1, 3_870_115)],
		failure: {
			reason: 'IMAGE_QUALITY',
			message: 'imagem com resolução insuficiente para a análise forense das camadas 2 e 3.',
		},
		metadata: { cnj: '0010099-73.2026.5.02.0003' },
	},
};

/** Resultado genérico de admissão para jobs de Cadastro criados durante a demo */
export const CREATED_CADASTRO_RESULT = admissionResult({
	nome: 'Gabriela Martins Freitas',
	cpf: '852.741.963-10',
	rg: '39.852.147-6 SSP/SP',
	nascimento: '19/08/1993',
	admissao: '01/10/2026',
	cargo: 'Analista de Departamento Pessoal',
	cbo: '4110-10',
	departamento: 'Gente & Gestão',
	salario: 'R$ 4.650,00',
	pis: '128.74196.35-2',
	ctps: '6320147 — série 0031/SP',
	endereco: {
		logradouro: 'Rua Augusta, 2310, apto 12',
		bairro: 'Jardins',
		cidade: 'São Paulo',
		uf: 'SP',
		cep: '01412-100',
	},
	dependentes: [],
});

const FALLBACK_PROFILE: MockJobProfile = {
	minutesAgo: 30,
	queueMs: 2_000,
	processingMs: 45_000,
	reviewMs: HOUR_MS,
	documents: [pdf('documento.pdf', FT.contratoTrabalho, 2, 250_000)],
};

// ── Estado de execução (jobs criados/reprocessados na demo) ─────────────────

interface JobRuntime {
	createdAtMs: number;
	startedAtMs: number | null;
	processedAtMs: number | null;
	retryCount: number;
}

const createdProfiles = new Map<string, MockJobProfile>();
const runtimeByJob = new Map<string, JobRuntime>();
/** Primeiro instante em que um job foi visto num status (evita datas "andando" a cada request) */
const firstSeenAt = new Map<string, number>();

function firstSeen(key: string): number {
	const existing = firstSeenAt.get(key);
	if (existing !== undefined) return existing;
	const now = Date.now();
	firstSeenAt.set(key, now);
	return now;
}

export function profileOf(
	jobId: string,
	entry: MockJobRosterEntry | undefined = findJobEntry(jobId),
): MockJobProfile {
	return (
		createdProfiles.get(jobId) ??
		MOCK_JOB_PROFILES[jobId] ??
		(entry ? generatedProfileOf(entry) : null) ??
		FALLBACK_PROFILE
	);
}

export function registerCreatedProfile(
	jobId: string,
	profile: MockJobProfile,
	createdAtMs: number,
) {
	createdProfiles.set(jobId, profile);
	runtimeByJob.set(jobId, {
		createdAtMs,
		startedAtMs: Date.now(),
		processedAtMs: null,
		retryCount: 0,
	});
}

/** Reprocessamento: reinicia o cronômetro mantendo a data de criação */
export function markRetryStarted(jobId: string, createdAtMs: number) {
	const previous = runtimeByJob.get(jobId);
	runtimeByJob.set(jobId, {
		createdAtMs,
		startedAtMs: Date.now(),
		processedAtMs: null,
		retryCount: (previous?.retryCount ?? 0) + 1,
	});
	for (const key of [...firstSeenAt.keys()]) {
		if (key.startsWith(`${jobId}:`)) firstSeenAt.delete(key);
	}
}

export function markProcessed(jobId: string) {
	const runtime = runtimeByJob.get(jobId);
	if (runtime) runtime.processedAtMs = Date.now();
}

// ── Visão consolidada de um job ─────────────────────────────────────────────

export interface MockJobDocument extends MockJobDocumentSeed {
	fileId: string;
}

export interface MockJobView {
	entry: MockJobRosterEntry;
	profile: MockJobProfile;
	status: JobStatusLabel;
	createdAtMs: number;
	createdAt: string;
	startedAt: string | null;
	processedAt: string | null;
	finishedAt: string | null;
	timings: JobTimings;
	pagesProcessed: number;
	documents: MockJobDocument[];
	failure: JobFailure | null;
	retryCount: number;
	person: JobCreatedBy;
}

function toIso(ms: number | null): string | null {
	return ms === null ? null : new Date(ms).toISOString();
}

export function jobView(entry: MockJobRosterEntry): MockJobView {
	const profile = profileOf(entry.id, entry);
	const status = currentJobStatus(entry.id) ?? entry.status;
	const runtime = runtimeByJob.get(entry.id);
	const now = Date.now();

	const createdAtMs =
		runtime?.createdAtMs ??
		MOCK_EPOCH_MS - entry.createdDaysAgo * DAY_MS - profile.minutesAgo * MINUTE_MS;
	let startedAtMs: number | null = runtime ? runtime.startedAtMs : createdAtMs + profile.queueMs;
	let processedAtMs: number | null = runtime
		? runtime.processedAtMs
		: (startedAtMs ?? 0) + profile.processingMs;
	let finishedAtMs: number | null = null;

	switch (status) {
		case 'Aguardando':
			startedAtMs = null;
			processedAtMs = null;
			break;
		case 'Em processamento':
			startedAtMs = Math.min(startedAtMs ?? now, now);
			processedAtMs = null;
			break;
		case 'Aguardando revisão':
		case 'Em revisão':
			processedAtMs = Math.min(processedAtMs ?? firstSeen(`${entry.id}:processed`), now);
			break;
		case 'Finalizado': {
			processedAtMs = Math.min(processedAtMs ?? firstSeen(`${entry.id}:processed`), now);
			const targetMs =
				entry.product === 'cadastro' ? processedAtMs : processedAtMs + profile.reviewMs;
			finishedAtMs = Math.min(targetMs, firstSeen(`${entry.id}:finished`));
			break;
		}
		case 'Falha':
			processedAtMs = Math.min(processedAtMs ?? now, now);
			finishedAtMs = processedAtMs;
			break;
	}

	const documents = profile.documents.map((document, index) => ({
		...document,
		fileId: `file-${entry.id}-${index + 1}`,
	}));
	const pagesProcessed = POST_PROCESSING_STATUSES.has(status)
		? documents.reduce((total, document) => total + document.pageCount, 0)
		: 0;
	const retryCount = runtime?.retryCount ?? (status === 'Falha' ? 1 : 0);
	const failure: JobFailure | null =
		status === 'Falha'
			? {
					reason: profile.failure?.reason ?? 'PROCESSING_ERROR',
					message: profile.failure?.message ?? 'Não foi possível concluir o processamento do job.',
					occurredAt: toIso(finishedAtMs) ?? new Date(now).toISOString(),
					retryCount,
					retryExhausted: retryCount >= 3,
				}
			: null;
	const person = MOCK_PEOPLE[entry.createdBy];

	return {
		entry,
		profile,
		status,
		createdAtMs,
		createdAt: new Date(createdAtMs).toISOString(),
		startedAt: toIso(startedAtMs),
		processedAt: toIso(processedAtMs),
		finishedAt: toIso(finishedAtMs),
		timings: {
			queueTimeMs: startedAtMs !== null ? Math.max(0, startedAtMs - createdAtMs) : null,
			processingTimeMs:
				startedAtMs !== null && processedAtMs !== null
					? Math.max(0, processedAtMs - startedAtMs)
					: null,
			totalTimeMs: finishedAtMs !== null ? Math.max(0, finishedAtMs - createdAtMs) : null,
		},
		pagesProcessed,
		documents,
		failure,
		retryCount,
		person: { id: person.id, name: person.name, email: person.email },
	};
}

// ── Consulta (lista, overview e dashboards) ─────────────────────────────────

export function productOfSetup(productSetupId: string | null | undefined): JobProductSlug | null {
	if (!productSetupId) return null;
	const products: JobProductSlug[] = ['cadastro', 'contestacao', 'basilisk'];
	return products.find((product) => productSetupId.endsWith(`-${product}`)) ?? null;
}

export interface MockSessionContext {
	productSetupId: string | null;
	personKey: MockPersonKey;
}

/** Contexto ativo da sessão mock (quando a request não informa produto) */
export function sessionContext(): MockSessionContext {
	try {
		const user = buildCurrentUser();
		const personEntry = Object.entries(MOCK_PEOPLE).find(([, person]) => person.id === user.id);
		return {
			productSetupId: user.productSetupId || null,
			personKey: (personEntry?.[0] as MockPersonKey | undefined) ?? 'operador',
		};
	} catch {
		return { productSetupId: null, personKey: 'operador' };
	}
}

export interface MockJobQuery {
	product: JobProductSlug | null;
	/** Setup (empresa + vertical) do produto; sem ele, lista o produto em todos os setups */
	productSetupId?: string | null;
	statuses?: ReadonlySet<JobStatusLabel> | null;
	source?: string | null;
	createdByUserId?: string | null;
	fromMs?: number | null;
	toMs?: number | null;
	search?: string | null;
}

export function parseDateMs(value: string | null): number | null {
	if (!value) return null;
	const ms = new Date(value).getTime();
	return Number.isNaN(ms) ? null : ms;
}

export function queryJobViews(query: MockJobQuery): MockJobView[] {
	const search = query.search?.trim().toLowerCase() ?? '';
	return allJobEntries()
		.filter((entry) => !query.product || entry.product === query.product)
		.filter((entry) => !query.productSetupId || entry.productSetupId === query.productSetupId)
		.map((entry) => jobView(entry))
		.filter((view) => {
			if (query.statuses && !query.statuses.has(view.status)) return false;
			if (query.source && view.entry.source !== query.source) return false;
			if (query.createdByUserId && view.person.id !== query.createdByUserId) return false;
			if (query.fromMs != null && view.createdAtMs < query.fromMs) return false;
			if (query.toMs != null && view.createdAtMs > query.toMs) return false;
			if (search) {
				const haystack = [
					view.entry.id,
					view.entry.description,
					view.person.name,
					view.profile.metadata?.cnj ?? '',
					view.profile.metadata?.legalboxContencioso ?? '',
					...view.documents.map((document) => document.filename),
				]
					.join(' ')
					.toLowerCase();
				if (!haystack.includes(search)) return false;
			}
			return true;
		})
		.sort((first, second) => second.createdAtMs - first.createdAtMs);
}

// ── Setup do produto (snapshot, franquia e cobrança) ────────────────────────

function setupOf(productSetupId: string): ProductSetupDetail | undefined {
	return productSetupsDb.find((setup) => setup.id === productSetupId);
}

function verticalSlugOf(productSetupId: string, product: JobProductSlug): string {
	return productSetupId.replace(/^ps-/, '').replace(new RegExp(`-${product}$`), '');
}

export function buildSetupSnapshot(
	productSetupId: string,
	product: JobProductSlug,
): JobDetailSetupSnapshot {
	const setup = setupOf(productSetupId);
	const client = clientsDb.find((candidate) => candidate.id === setup?.client.id);
	const verticalSlug = verticalSlugOf(productSetupId, product);
	const verticalInfo = MOCK_VERTICAL_INFO[verticalSlug];
	const catalog = MOCK_CATALOG[product];

	return {
		id: productSetupId,
		client: {
			id: client?.id ?? MOCK_CONTEXT_CLIENT.id,
			cnpj: client?.cnpj ?? MOCK_CONTEXT_CLIENT.cnpj,
			logo: client?.logo ?? MOCK_CONTEXT_CLIENT.logo,
			name: client?.name ?? MOCK_CONTEXT_CLIENT.name,
		},
		vertical: {
			id: setup?.vertical.id ?? verticalInfo?.id ?? `vert-${verticalSlug}`,
			name: setup?.vertical.name ?? verticalInfo?.name ?? verticalSlug,
			description: '',
		},
		productCatalog: {
			id: catalog.id,
			name: catalog.name,
			slug: catalog.slug,
			description: catalog.description ?? '',
		},
		webhookUrl: setup?.webhookUrl ?? null,
		monthlyLimit: setup?.monthlyLimit ?? null,
		hasMonthlyLimit: setup?.hasMonthlyLimit ?? false,
		limitType: setup?.limitType ?? 'JOBS',
		isEnabled: setup?.isEnabled ?? true,
		productBillingSetups: (setup?.billingTiers ?? []).map((tier) => ({
			id: tier.id,
			aiEngine: {
				id: tier.aiEngine.id,
				name: tier.aiEngine.name,
				modelId: tier.aiEngine.modelId,
				provider: tier.aiEngine.provider,
			},
			baseValue: String(tier.baseValue),
			pageInitial: tier.pageInitial,
			pageFinal: tier.pageFinal,
		})),
	};
}

export function buildBilling(view: MockJobView): JobBilling | null {
	if (!POST_PROCESSING_STATUSES.has(view.status)) return null;
	const setup = setupOf(view.entry.productSetupId);
	const tier = setup?.billingTiers[0];
	if (!tier) return null;

	const chargedValueBrl =
		setup.limitType === 'JOBS' && setup.monthlyLimit
			? tier.baseValue / setup.monthlyLimit
			: view.pagesProcessed * (tier.excessChargeValue ?? 0.5);
	const roundedBrl = Math.round(chargedValueBrl * 100) / 100;

	return {
		appliedTier: {
			id: tier.id,
			pageInitial: tier.pageInitial,
			pageFinal: tier.pageFinal,
			baseValue: tier.baseValue,
			excessChargeValue: tier.excessChargeValue ?? 0,
			aiEngineId: tier.aiEngine.id,
		},
		chargedValueBrl: roundedBrl,
		chargedValueUsd: Math.round((roundedBrl / 5.42) * 100) / 100,
	};
}

/**
 * Consumo do mês feito por integrações cujos jobs não aparecem na caixa de entrada
 * (lotes de API expurgados após a entrega por webhook). Somado ao consumo dos jobs
 * listados, deixa a franquia em ~60–70% no fim do mês: Cadastro em páginas (limite
 * 5.000), Contestação em jobs (limite 300); o Basilisk não tem franquia.
 */
const MONTHLY_USAGE_BASELINE: Record<JobProductSlug, number> = {
	cadastro: 3_184,
	contestacao: 162,
	basilisk: 47,
};

export function buildMonthlyUsage(
	productSetupId: string | null,
	product: JobProductSlug | null,
): JobsMonthlyUsage {
	const setup = productSetupId ? setupOf(productSetupId) : undefined;
	const isPages = setup?.limitType === 'PAGES';
	const monthStart = new Date();
	monthStart.setDate(1);
	monthStart.setHours(0, 0, 0, 0);

	const monthJobs = queryJobViews({ product, productSetupId, fromMs: monthStart.getTime() }).filter(
		(view) => view.status !== 'Falha',
	);
	const listedUsage = isPages
		? monthJobs.reduce((total, view) => total + view.pagesProcessed, 0)
		: monthJobs.length;
	// A parcela das integrações cresce ao longo do mês (proporcional aos dias corridos)
	const today = new Date();
	const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
	const baseline = product
		? Math.round((MONTHLY_USAGE_BASELINE[product] * today.getDate()) / daysInMonth)
		: 0;
	const used = listedUsage + baseline;
	const limit = setup?.hasMonthlyLimit ? setup.monthlyLimit : null;

	return {
		limit,
		used,
		remaining: limit !== null ? Math.max(0, limit - used) : null,
		type: isPages ? 'pages' : 'jobs',
		limitType: isPages ? LimitType.PAGES : LimitType.JOBS,
	};
}

// ── Mapeamentos para os contratos da API ────────────────────────────────────

function fileTypeOf(fileTypeId: string): JobFileType {
	const fileType = fileTypesDb.find((candidate) => candidate.id === fileTypeId);
	return {
		id: fileTypeId,
		name: fileType?.name ?? 'Documento',
		description: fileType?.description ?? '',
		createdAt: fileType?.createdAt ?? '2026-01-10T12:00:00.000Z',
		createdBy: fileType?.createdBy?.id ?? null,
		updatedAt: fileType?.updatedAt ?? '2026-08-20T12:00:00.000Z',
		updatedBy: fileType?.updatedBy?.id ?? null,
	};
}

function checksumOf(fileId: string): string {
	let hash = 0;
	for (const character of fileId) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
	return hash.toString(16).padStart(8, '0').repeat(8);
}

export function toJobFiles(view: MockJobView, withFileType: boolean): JobFile[] {
	return view.documents.map((document) => ({
		id: document.fileId,
		jobId: view.entry.id,
		fileTypeId: document.fileTypeId,
		name: document.filename,
		mimeType: 'application/pdf',
		sizeBytes: document.sizeBytes,
		pageCount: document.pageCount,
		checksumSha256: checksumOf(document.fileId),
		provider: 's3',
		visibility: 'private',
		category: 'input',
		createdAt: view.createdAt,
		createdBy: view.person.id,
		updatedAt: view.createdAt,
		updatedBy: view.person.id,
		deletedAt: null,
		deletedBy: null,
		...(withFileType
			? {
					fileType: {
						...fileTypeOf(document.fileTypeId),
						name: document.fileTypeName ?? fileTypeOf(document.fileTypeId).name,
					},
				}
			: {}),
	}));
}

export function toJob(view: MockJobView, include: string | null): Job {
	const includes = (include ?? '').split(',').map((part) => part.trim());
	const withFileType = includes.includes('fileType');
	const withFiles = withFileType || includes.includes('files');

	return {
		id: view.entry.id,
		productSetupId: view.entry.productSetupId,
		description: view.entry.description,
		status: view.status,
		source: view.entry.source,
		documentsCount: view.documents.length,
		pagesProcessed: view.pagesProcessed,
		isOverLimit: false,
		metadata: { ...view.profile.metadata },
		startedAt: view.startedAt,
		finishedAt: view.finishedAt,
		processingTimeMs: view.timings.processingTimeMs,
		failureReason: view.failure?.message ?? null,
		createdAt: view.createdAt,
		createdBy: view.person,
		...(withFiles ? { files: toJobFiles(view, withFileType) } : {}),
	};
}

function toDetailDocuments(view: MockJobView): JobDetailDocument[] {
	return view.documents.map((document) => ({
		fileId: document.fileId,
		filename: document.filename,
		fileTypeName: document.fileTypeName ?? fileTypeOf(document.fileTypeId).name,
		mimeType: 'application/pdf',
		sizeBytes: document.sizeBytes,
		pageCount: document.pageCount,
		uploadedAt: view.createdAt,
		downloadUrl: MOCK_SAMPLE_PDF_URL,
	}));
}

function contestacaoResult(view: MockJobView): ContestacaoJobResult | null {
	if (!POST_PROCESSING_STATUSES.has(view.status)) return null;
	const reviewed = view.status === 'Finalizado';
	return {
		selection: {
			warnings: [
				'Documento de subsídio sem assinatura identificada: verifique a validade antes de protocolar.',
			],
			total_theses: 9,
			approved_count: reviewed ? 7 : 0,
			rejected_count: reviewed ? 2 : 0,
		},
	};
}

/** `result` do job no detalhe (GET /jobs/:id) */
export function jobResultOf(view: MockJobView): Record<string, unknown> | null {
	switch (view.entry.product) {
		case 'cadastro':
			return view.status === 'Finalizado' ? (view.profile.result ?? null) : null;
		case 'contestacao':
			return contestacaoResult(view);
		default:
			// O laudo do Basilisk é hidratado por GET /jobs/:id/basilisk/review
			return null;
	}
}

export function toJobDetail(view: MockJobView): JobDetail {
	const id = view.entry.id;
	const result = jobResultOf(view);
	return {
		id,
		status: view.status,
		source: view.entry.source,
		description: view.entry.description,
		createdAt: view.createdAt,
		startedAt: view.startedAt,
		finishedAt: view.finishedAt,
		createdBy: view.person,
		isOverLimit: false,
		pagesProcessed: view.pagesProcessed,
		documents: toDetailDocuments(view),
		setupSnapshot: buildSetupSnapshot(view.entry.productSetupId, view.entry.product),
		timings: view.timings,
		resultAvailable: POST_PROCESSING_STATUSES.has(view.status),
		result,
		failure: view.failure,
		billing: buildBilling(view),
		_links: {
			self: `/jobs/${id}`,
			status: `/jobs/${id}/status`,
			result: `/jobs/${id}/result`,
			resultDownloads: {
				xlsx: `/jobs/${id}/result/download?format=xlsx`,
				json: `/jobs/${id}/result/download?format=json`,
				xml: `/jobs/${id}/result/download?format=xml`,
			},
		},
	};
}

/** Campos extraídos com a origem no PDF (página + bbox em pontos, origem no topo) */
export function toResultFields(view: MockJobView): JobResultField[] {
	const result = view.profile.result ?? {};
	const firstDocument = view.documents[0];
	return Object.entries(result).map(([key, value], index) => {
		if (!firstDocument) return { key, value, source: null };
		const page = Math.min(firstDocument.pageCount, 1 + Math.floor(index / 12));
		const row = index % 12;
		const top = 96 + row * 52;
		return {
			key,
			value,
			source: { fileId: firstDocument.fileId, page, bbox: [72, top, 420, top + 24] },
		};
	});
}
